import { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { formatEURDecimal, SPANISH_MONTHS_SHORT } from "../lib/format";
import { Plus, Trash2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

const QUINCENAS = [];
for (let m = 1; m <= 12; m++) for (let q = 1; q <= 2; q++) QUINCENAS.push({ key: `${m}-${q}`, month: m, quincena: q });

function CellEditor({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const inputRef = useRef(null);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);
  useEffect(() => { setVal(value); }, [value]);
  const commit = async () => {
    const n = parseFloat(val) || 0;
    if (n !== value) await onSave(n);
    setEditing(false);
  };
  if (editing) {
    return (
      <input
        ref={inputRef} type="number" step="0.01"
        className="w-full text-right font-mono-num text-xs border border-black px-1 py-0.5 focus:outline-none"
        value={val} onChange={(e) => setVal(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); else if (e.key === "Escape") { setVal(value); setEditing(false); } }}
      />
    );
  }
  return (
    <div
      onClick={() => setEditing(true)}
      className={`min-h-[24px] cursor-pointer text-right font-mono-num text-xs px-1 ${value ? "text-red-700" : "text-[var(--ink-muted)]/30 hover:text-[var(--ink-muted)] hover:bg-[var(--hover)]"}`}
      data-testid="fixed-cell"
    >
      {value ? `−${formatEURDecimal(value)}` : "+"}
    </div>
  );
}

export default function GastosFijos() {
  const [year, setYear] = useState(2026);
  const [data, setData] = useState({ expenses: [], categories: [], category_totals: [] });
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("Salariales");
  const [newName, setNewName] = useState("");
  const [editingMeta, setEditingMeta] = useState({});
  const [showPastMonths, setShowPastMonths] = useState(() => localStorage.getItem("fixed_show_past") === "true");

  const togglePast = (v) => {
    setShowPastMonths(v);
    localStorage.setItem("fixed_show_past", String(v));
  };

  const visibleQuincenas = (() => {
    const cur = data.current_quincena;
    if (showPastMonths || !cur || year !== cur.year) return QUINCENAS;
    return QUINCENAS.filter(q => q.month > cur.month || (q.month === cur.month && q.quincena >= cur.quincena));
  })();

  const load = () => {
    setLoading(true);
    api.get(`/fixed-expenses?year=${year}&section=estructura`).then(r => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const setCell = async (feId, m, q, amount) => {
    try { await api.put(`/fixed-expenses/${feId}/cell`, { year, month: m, quincena: q, amount }); load(); }
    catch (e) { toast.error("Error al guardar"); }
  };

  const addExpense = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await api.post("/fixed-expenses", { name: newName.trim(), category: activeCat, order: 999 });
      setNewName("");
      load();
    } catch (e) { toast.error("Error"); }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm("¿Eliminar esta línea y todos sus importes?")) return;
    try { await api.delete(`/fixed-expenses/${id}`); load(); }
    catch (e) { toast.error("Error"); }
  };

  const saveMeta = async (id) => {
    const m = editingMeta[id];
    try {
      await api.patch(`/fixed-expenses/${id}`, { name: m.name, notes: m.notes });
      const c = { ...editingMeta }; delete c[id]; setEditingMeta(c);
      load();
    } catch (e) { toast.error("Error"); }
  };

  // Filter expenses to active category
  const catExpenses = data.expenses.filter(e => (e.category || "Otros") === activeCat);
  const catTotal = data.category_totals.find(c => c.category === activeCat)?.total || 0;
  const grandTotal = data.category_totals.reduce((a, c) => a + c.total, 0);
  const columnTotal = (key) => catExpenses.reduce((acc, r) => acc + (r.cells[key] || 0), 0);
  const categories = data.categories || [];

  return (
    <div className="p-8" data-testid="gastos-fijos-page">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)]">Gastos fijos</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Costes recurrentes y obligaciones.</h1>
          <p className="text-sm text-[var(--ink-muted)] mt-2 max-w-2xl">
            Estructura del Excel PRESUPUESTO_2026: 9 sub-pestañas. Cada importe entra en el cashflow como gasto real en su quincena.
            Haz clic en cualquier celda para editar.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs" data-testid="gf-toggle-past-wrapper">
            <input type="checkbox" checked={showPastMonths} onChange={(e) => togglePast(e.target.checked)} data-testid="gf-toggle-past" />
            <span>Mostrar meses anteriores</span>
          </label>
          <select data-testid="gf-year" className="border border-[var(--border)] bg-white px-3 py-2 text-sm font-mono-num focus:outline-none focus:border-black" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[year-2, year-1, year, year+1, year+2].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="border border-[var(--border)] bg-black text-white p-4">
          <div className="font-mono-num text-[10px] uppercase tracking-widest text-white/60">Total {year}</div>
          <div className="font-display font-black text-xl mt-1">−{formatEURDecimal(grandTotal)}</div>
          <div className="text-[10px] font-mono-num text-white/60">{data.expenses.length} líneas</div>
        </div>
        {data.category_totals.slice(0, 4).map(c => (
          <div key={c.category} className="border border-[var(--border)] bg-white p-4">
            <div className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)] truncate">{c.category}</div>
            <div className="font-display font-bold text-lg mt-1">{c.total ? `−${formatEURDecimal(c.total)}` : "—"}</div>
            <div className="text-[10px] font-mono-num text-[var(--ink-muted)]">{c.count} líneas</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-0 overflow-x-auto" data-testid="gf-tabs">
        {categories.map(cat => {
          const t = data.category_totals.find(c => c.category === cat)?.total || 0;
          const active = activeCat === cat;
          return (
            <button
              key={cat}
              onClick={() => setActiveCat(cat)}
              data-testid={`gf-tab-${cat}`}
              className={`px-4 py-2.5 text-xs uppercase tracking-wider font-mono-num border-b-2 transition-colors whitespace-nowrap ${
                active ? "bg-white border-black text-black" : "bg-[var(--hover)] border-transparent text-[var(--ink-muted)] hover:text-[var(--ink)]"
              }`}
            >
              {cat}
              {t > 0 && <span className="ml-2 text-[10px] text-red-700">−{formatEURDecimal(t)}</span>}
            </button>
          );
        })}
      </div>

      {/* Add new line bar - sticky inside the tab */}
      <form onSubmit={addExpense} className="bg-white border border-[var(--border)] border-t-0 p-3 flex gap-2 items-center" data-testid="gf-new-form">
        <span className="text-[10px] uppercase tracking-widest font-mono-num text-[var(--ink-muted)] whitespace-nowrap">+ Añadir a <strong className="text-[var(--ink)]">{activeCat}</strong></span>
        <input
          placeholder="Nombre del concepto (ej. nuevo empleado, software, etc.)…"
          className="flex-1 border border-[var(--border)] bg-white px-3 py-1.5 text-sm focus:outline-none focus:border-black"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          data-testid="gf-new-name"
        />
        <button type="submit" disabled={!newName.trim()} className="bg-black text-white px-4 py-1.5 text-sm inline-flex items-center gap-1.5 hover:bg-gray-800 disabled:opacity-40" data-testid="gf-new-add">
          <Plus size={14}/>Crear línea
        </button>
      </form>

      <div className="border border-[var(--border)] border-t-0 bg-white overflow-auto max-h-[60vh]">
        <table className="cashflow-grid text-xs w-max min-w-full" data-testid="gf-table">
          <thead>
            <tr>
              <th className="sticky-col text-left px-3 py-3 font-mono-num text-[10px] uppercase tracking-widest min-w-[280px]">Concepto</th>
              {visibleQuincenas.map(q => (
                <th key={q.key} className="px-2 py-3 font-mono-num text-[10px] uppercase tracking-widest text-right min-w-[90px]">
                  <div>{SPANISH_MONTHS_SHORT[q.month-1]}</div>
                  <div className="text-[var(--ink-muted)]">Q{q.quincena}</div>
                </th>
              ))}
              <th className="px-3 py-3 font-mono-num text-[10px] uppercase tracking-widest text-right min-w-[110px]">Total</th>
              <th className="px-2 py-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={visibleQuincenas.length + 3} className="px-4 py-6 text-center text-[var(--ink-muted)]">Cargando…</td></tr>
            ) : catExpenses.length === 0 ? (
              <tr><td colSpan={visibleQuincenas.length + 3} className="px-4 py-8 text-center text-[var(--ink-muted)] text-xs" data-testid="gf-empty">
                Sin líneas en esta categoría. Añade una abajo.
              </td></tr>
            ) : catExpenses.map(fe => {
              const ed = editingMeta[fe.id];
              return (
                <tr key={fe.id} className="hover:bg-[var(--hover)]" data-testid={`fixed-row-${fe.id}`}>
                  <td className="sticky-col bg-white hover:bg-[var(--hover)] px-3 py-2">
                    {ed ? (
                      <div className="flex flex-col gap-1">
                        <input className="border border-[var(--border)] px-2 py-1 text-xs focus:outline-none focus:border-black" value={ed.name} onChange={(e) => setEditingMeta({ ...editingMeta, [fe.id]: { ...ed, name: e.target.value } })} />
                        <input placeholder="Notas (opcional)" className="border border-[var(--border)] px-2 py-1 text-xs focus:outline-none focus:border-black" value={ed.notes || ""} onChange={(e) => setEditingMeta({ ...editingMeta, [fe.id]: { ...ed, notes: e.target.value } })} />
                        <div className="flex gap-1">
                          <button onClick={() => saveMeta(fe.id)} className="bg-black text-white p-1 hover:bg-gray-800"><Check size={12} /></button>
                          <button onClick={() => { const c = { ...editingMeta }; delete c[fe.id]; setEditingMeta(c); }} className="border border-[var(--border)] p-1 hover:bg-[var(--hover)]"><X size={12} /></button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="text-xs font-bold tracking-wide">{fe.name}</div>
                        {fe.notes && <div className="text-[10px] text-[var(--ink-muted)] font-mono-num">{fe.notes}</div>}
                      </div>
                    )}
                  </td>
                  {visibleQuincenas.map(q => (
                    <td key={q.key} className="px-1 py-1 align-middle" data-testid={`gf-cell-${fe.id}-${q.key}`}>
                      <CellEditor value={fe.cells[q.key] || 0} onSave={(v) => setCell(fe.id, q.month, q.quincena, v)} />
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right font-mono-num font-bold text-red-700">
                    {fe.total > 0 ? `−${formatEURDecimal(fe.total)}` : ""}
                  </td>
                  <td className="px-1 py-2 text-right">
                    <div className="flex gap-1 justify-end">
                      <button
                        onClick={() => setEditingMeta({ ...editingMeta, [fe.id]: { name: fe.name, notes: fe.notes || "" } })}
                        className="border border-[var(--border)] text-[var(--ink-muted)] hover:text-[var(--ink)] hover:bg-[var(--hover)] p-1.5"
                        title="Editar nombre"
                        data-testid={`gf-edit-${fe.id}`}
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={() => deleteExpense(fe.id)}
                        className="border border-red-200 text-red-600 hover:text-white hover:bg-red-700 hover:border-red-700 p-1.5"
                        title="Eliminar línea"
                        data-testid={`gf-delete-${fe.id}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {/* Subtotal row */}
            <tr className="bg-zinc-100 border-t-2 border-[var(--ink)]">
              <td className="sticky-col bg-zinc-100 px-3 py-2 text-[10px] uppercase tracking-widest font-mono-num font-bold">Subtotal · {activeCat}</td>
              {visibleQuincenas.map(q => {
                const v = columnTotal(q.key);
                return <td key={q.key} className="px-2 py-2 text-right font-mono-num font-bold text-red-700">{v ? `−${formatEURDecimal(v)}` : ""}</td>;
              })}
              <td className="px-3 py-2 text-right font-mono-num font-bold text-red-700">{catTotal > 0 ? `−${formatEURDecimal(catTotal)}` : ""}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      <form onSubmit={addExpense} className="hidden">
        {/* duplicated form removed - moved above the table */}
      </form>
    </div>
  );
}
